import React from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ParticlesBackground from "../components/ParticlesBackground";
import highlightedWorksData from '../data/highlights.json';
import Navigation from '../components/Navigation';
import MonetModal from '../components/MonetModal';
import FooterCentered from '../components/FooterCentered';
import { motion } from 'framer-motion';

export default function WorkDetail() {
    const { workName } = useParams();
    const [email, setEmail] = useState("");
    const [selectedWork, setSelectedWork] = useState(null);

    const handleEmail = (e) => {
        e.preventDefault();
        console.log(email);
    }

    const project = highlightedWorksData.find(
        (p) => p.pageName.toLowerCase() === workName.toLowerCase()
    );

    if (!project) {
        return (
            <div className="bg-void min-h-screen">
                <Navigation />
                <div className="max-w-2xl mx-auto py-8 px-6 pt-32">
                    <h1 className="font-syne text-3xl font-bold text-text-primary mb-4">Work Not Found</h1>
                    <p className="font-outfit text-text-secondary">
                        We could not find any project named <strong className="text-text-primary">{workName}</strong>.
                    </p>
                    <Link to="/works" className="mt-6 inline-block text-accent font-outfit text-sm hover:underline">
                        Back to all work
                    </Link>
                </div>
            </div>
        );
    }

    const [img1, img2, img3, img4] = project.projectImages;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-void min-h-screen"
        >
            <ParticlesBackground id="tsparticles" />
            <Navigation />

            <div className="mx-auto max-w-3xl px-6 py-8 pt-28 sm:pt-36 mt-8 lg:mt-0">
                <p className="text-xs font-outfit font-semibold tracking-[0.2em] uppercase text-accent mb-2">
                    {project.workType}
                </p>
                <h1 className="font-syne text-4xl font-bold text-text-primary mb-8">
                    {project.name}
                </h1>

                <div className="space-y-4">
                    {img1 && (
                        <img
                            src={img1}
                            alt={`${project.name} screenshot 1`}
                            className="rounded-xl border border-border-subtle"
                        />
                    )}
                    {img2 && (
                        <img
                            src={img2}
                            alt={`${project.name} screenshot 2`}
                            className="rounded-xl border border-border-subtle"
                        />
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {img3 && (
                            <img
                                src={img3}
                                alt={`${project.name} screenshot 3`}
                                className="rounded-xl border border-border-subtle"
                            />
                        )}
                        {img4 && (
                            <img
                                src={img4}
                                alt={`${project.name} screenshot 4`}
                                className="rounded-xl border border-border-subtle"
                            />
                        )}
                    </div>
                </div>

                <section className="mt-12 mb-12">
                    <h2 className="font-syne text-2xl font-bold text-text-primary mb-4">
                        {project.subheading}
                    </h2>
                    <p className="font-outfit text-sm text-text-secondary whitespace-pre-line leading-relaxed mb-8">
                        {project.description}
                    </p>
                    <div>
                        {project.name === "Monet.ai" ? (
                            <button
                                onClick={() => {
                                    setSelectedWork(project);
                                }}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent text-void font-outfit font-semibold text-sm hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-all duration-300"
                            >
                                {project.cta || "Explore Work"}
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                                </svg>
                            </button>
                        ) : (
                            <a
                                href={project.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent text-void font-outfit font-semibold text-sm hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-all duration-300"
                            >
                                {project.cta || "Explore Work"}
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                                </svg>
                            </a>
                        )}
                    </div>
                </section>

                <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12 pb-16">
                    <div>
                        <h3 className="font-syne text-lg font-bold text-text-primary mb-4">
                            Project Scope
                        </h3>
                        <ul className="font-outfit text-sm text-text-secondary space-y-2">
                            {project.projectTimeline.map((item, index) => (
                                <li key={index} className="flex items-start gap-2">
                                    <span className="text-accent mt-1 flex-shrink-0">&#8226;</span>
                                    {item}
                                </li>
                            ))}
                            {project.projectScope.map((item, index) => (
                                <li key={`scope-${index}`} className="flex items-start gap-2">
                                    <span className="text-accent mt-1 flex-shrink-0">&#8226;</span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h3 className="font-syne text-lg font-bold text-text-primary mb-4">
                            Software
                        </h3>
                        <ul className="font-outfit text-sm text-text-secondary space-y-2">
                            {project.software.map((tool, index) => (
                                <li key={index} className="flex items-start gap-2">
                                    <span className="text-accent mt-1 flex-shrink-0">&#8226;</span>
                                    {tool}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </div>

            <MonetModal
                open={selectedWork?.name === 'Monet.ai'}
                setOpen={setSelectedWork}
                email={email}
                setEmail={setEmail}
                handleEmail={handleEmail}
            />

            <FooterCentered />
        </motion.div>
    );
}
